import { useState, useEffect } from 'react';
import styles from './DraftBankingDetailInfoComponent.module.css';
import { stockLabel } from '../common/stockType';

// 표시할 필드 정의 (15개) — 순서대로 렌더링
const FIELDS = [
    { key: 'Organoid_Info_Key',      label: 'Organoid Info Key' },
    { key: 'PatientCode',            label: 'Patient Code' },
    { key: 'Master',                 label: 'Stock' },
    { key: 'LabCode',                label: 'Lab Code' },
    { key: 'PassageNo',              label: 'Passage No.' },
    { key: 'Keeper',                 label: 'Keeper' },
    { key: 'FreezingMedia',          label: 'Freezing Media' },
    { key: 'FreezingDate',           label: 'Freezing Date' },
    { key: 'domePerVialOrCellNumber',label: 'Dome/Vial or Cell number' },
    { key: 'CultureMedia',           label: 'Culture Media' },
    { key: 'Mycoplasma',             label: 'Mycoplasma' },
    { key: 'thawingNo',              label: 'Thawing No.' },
    { key: 'annotation',             label: 'Note' },
    { key: 'Exporter',               label: 'Exporter' },
    { key: 'ExporteDate',            label: 'Export Date' },
];

const POSITION_FIELDS = ['Vessel', 'Line', 'Box', 'Position'];

const hashmarkToSharp = (v) => (v ?? '').toString().replace(/hashmark/g, '#');
// [2026-09-09] Master/Working 2분류 → MCB/WCB/ARB 3분류
const mapMaster = (val) => stockLabel(val);

// 필드별 표시 값 변환 (원본 동작 유지)
const formatFieldValue = (key, raw) => {
    const str = raw != null ? String(raw) : '';
    if (key === 'Master') return mapMaster(str || '0');
    if (key === 'Organoid_Info_Key') return hashmarkToSharp(str);
    return str;
};

// ── parseQuery (원본 그대로 — SQL 파서이므로 절대 수정 금지) ──────────────────
const parseQuery = (query = '') => {
    const updates = {};
    if (!query) return updates;

    const normalized = query.replace(/\s+/g, ' ').trim();

    if (/insert\s+into/i.test(normalized) && /values/i.test(normalized)) {
        const insertRe = /insert\s+into\s+[`"]?(?:\w+\.)?[`"]?[\w`"]+\s*\(([^)]+)\)\s*values\s*\(([\s\S]+)\)/i;
        const match = normalized.match(insertRe);
        if (!match) return updates;

        const keys = match[1].split(',').map(k => k.trim().replace(/[`'"]/g, ''));
        const valuesRaw = match[2].trim();
        const values = [];
        let cur = '', inQuotes = false, parenCount = 0;
        for (let ch of valuesRaw) {
            if (ch === "'" && !inQuotes) { inQuotes = true; cur += ch; }
            else if (ch === "'" && inQuotes) { inQuotes = false; cur += ch; }
            else if (!inQuotes && ch === '(') { parenCount++; cur += ch; }
            else if (!inQuotes && ch === ')') { parenCount--; cur += ch; }
            else if (!inQuotes && ch === ',' && parenCount === 0) {
                values.push(cur.trim().replace(/^'+|'+$/g, ''));
                cur = '';
            } else { cur += ch; }
        }
        if (cur) values.push(cur.trim().replace(/^'+|'+$/g, ''));

        keys.forEach((key, i) => { updates[key] = values[i] ?? ''; });
    } else if (/update\s+/i.test(normalized) && /set\s+/i.test(normalized)) {
        const setRe = /set\s+(.+?)\s+where/i;
        const m = normalized.match(setRe);
        if (m) {
            m[1].split(/,(?=(?:[^']*'[^']*')*[^']*$)/).forEach(pair => {
                const [rawKey, rawVal] = pair.split('=');
                const key = rawKey.trim().replace(/['`"]/g, '');
                const val = rawVal.trim().replace(/['`"]/g, '');
                updates[key] = val;
            });
        }
    }

    return updates;
};

const fetchBankingInfo = (body) =>
    fetch('/api/getBankingInfo', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
    }).then(res => res.json());

function DraftBankingDetailInfoComponent(props) {
    const [bankingList, setBankingList] = useState([]);
    const [updatedDataList, setUpdatedDataList] = useState([]);

    useEffect(() => {
        const { bundleId, targetKey, tempQuery, draftDocumentId, original_data } = props.selectedDraft;
        const { value } = props;

        // 1) original_data가 있으면 JSON 파싱하여 화면에 표시
        if (original_data) {
            let parsed = null;
            try {
                parsed = JSON.parse(original_data);
            } catch (e) {
                console.error('원본 JSON 파싱 실패:', e);
            }
            const originalObj = parsed?.data ?? parsed;
            setBankingList(originalObj ? [originalObj] : []);
        } else {
            // 2) original_data가 없으면 DB에서 fetch (bundleId 유무에 따라 body 분기)
            const body = bundleId ? { bundleId } : { param: value ?? targetKey };
            fetchBankingInfo(body)
                .then(data => setBankingList(data?.length ? data : []))
                .catch(err => {
                    console.error('getBankingInfo 에러:', err);
                    setBankingList([]);
                });
        }

        // 3) updatedDataList 세팅
        if (bundleId) {
            fetch('/api/getDraftByBundleId', {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ bundleId }),
            })
                .then(res => res.json())
                .then(drafts => {
                    setUpdatedDataList(drafts.map(d => ({
                        draftId: d.draftDocumentId,
                        updates: parseQuery(d.tempQuery),
                    })));
                })
                .catch(err => {
                    console.error('getDraftByBundleId 에러:', err);
                    setUpdatedDataList([]);
                });
        } else {
            setUpdatedDataList([{ draftId: draftDocumentId, updates: parseQuery(tempQuery) }]);
        }
    }, [props.value, props.selectedDraft]);

    // 원본 → 업데이트 표시 셀 (renderField, renderPositionList에서 공통 사용)
    const renderDiffCell = (original, updated, hasUpdate, hasOriginal) => {
        if (!hasOriginal) return updated || 'N/A';
        if (hasUpdate) {
            return (
                <>
                    <span className={styles.originalValue}>{original || 'N/A'}</span>
                    {' '}→{' '}
                    <span className={styles.updatedValue}>{updated || 'N/A'}</span>
                </>
            );
        }
        return original || updated || 'N/A';
    };

    // ── 필드 1개 렌더링 ────────────────────────────────────────────
    const renderField = ({ key, label }) => {
        const originalRaw = bankingList[0]?.[key];
        const updatedRaw  = updatedDataList[0]?.updates?.[key];

        const original = formatFieldValue(key, originalRaw);
        const updated  = formatFieldValue(key, updatedRaw);

        const isDraftApproved = props.selectedDraft.status === 'approved';
        const isUpdated = updatedRaw != null && original !== updated;

        // 원본이 없거나 승인된 기안: updated만 표시
        if (!bankingList[0] || isDraftApproved) {
            return (
                <div className={styles.fieldRow} key={key}>
                    <div className={styles.fieldLabel}>{label}</div>
                    <div className={styles.fieldValue}>{updated || 'N/A'}</div>
                </div>
            );
        }

        return (
            <div className={styles.fieldRow} key={key}>
                <div className={styles.fieldLabel}>{label}</div>
                <div className={styles.fieldValue}>
                    {isUpdated ? (
                        <>
                            <span className={styles.originalValue}>{original || 'N/A'}</span>
                            {' '}→{' '}
                            <span className={styles.updatedValue}>{updated || 'N/A'}</span>
                        </>
                    ) : (original || 'N/A')}
                </div>
            </div>
        );
    };

    // ── 위치 리스트 렌더링 ────────────────────────────────────────────
    const renderPositionList = () => {
        const original = bankingList[0] || null;
        const hasOriginal = original !== null;

        return updatedDataList.map((draft, idx) => (
            <div key={idx} className={styles.vesselGroup}>
                {POSITION_FIELDS.map(field => {
                    const orig = hasOriginal && original[field] != null ? String(original[field]) : '';
                    const updated = draft.updates[field] ?? '';
                    const hasUpdate = draft.updates[field] != null && updated !== orig;
                    return (
                        <div key={field} className={styles.fieldRow}>
                            <div className={styles.fieldLabel}>{field}</div>
                            <div className={styles.fieldValue}>
                                {renderDiffCell(orig, updated, hasUpdate, hasOriginal)}
                            </div>
                        </div>
                    );
                })}
                <hr />
            </div>
        ));
    };

    return (
        <div className={styles.main}>
            <div className={styles.draftBankingTitle}>Banking Information</div>
            <div className={styles.bankingInfoInDraft}>
                {FIELDS.map(renderField)}
            </div>
            <div className={styles.vesselListTitle}>Sample Locations</div>
            {renderPositionList()}
        </div>
    );
}

export default DraftBankingDetailInfoComponent;